import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Film, CheckCircle2, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import VideoPlayer from "@/components/VideoPlayer";
import VideoThumbnail from "@/components/VideoThumbnail";

function fmtDuration(s) {
  if (!s) return null;
  const m = Math.floor(s / 60); const r = Math.floor(s % 60);
  return `${m}:${r.toString().padStart(2, "0")}`;
}

export default function VideoLibrary() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/student/videos");
      setVideos(data || []);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onWatched = (id) => {
    setVideos((vs) => vs.map((v) => v.id === id ? { ...v, watched: true } : v));
  };

  return (
    <>
      {loading ? (
        <div data-testid="video-library-loading" className="text-stone">Loading…</div>
      ) : videos.length === 0 ? (
        <div data-testid="student-videos-empty" className="p-10 bg-white rounded-3xl card-tactile text-center">
          <Film className="w-10 h-10 text-pebble mx-auto mb-3" />
          <div className="font-heading text-lg font-bold text-ink">No lessons yet</div>
          <div className="text-stone text-sm">Your teacher hasn't uploaded any videos yet.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="student-videos-grid">
          {videos.map((v) => (
            <button
              key={v.id}
              data-testid={`student-video-card-${v.id}`}
              onClick={() => setActive(v)}
              className="text-left p-0 bg-white rounded-3xl card-tactile overflow-hidden hover:-translate-y-1 transition-transform"
            >
              <div className="aspect-video relative">
                <VideoThumbnail videoId={v.id} youtubeId={v.youtube_id} hasThumbnail={v.has_thumbnail} className="w-full h-full" />
                {v.watched && (
                  <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-leaf text-white text-xs font-bold">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Watched
                  </span>
                )}
                {fmtDuration(v.duration_seconds) && (
                  <span className="absolute bottom-3 right-3 px-2 py-0.5 rounded-md bg-black/70 text-white text-xs font-bold">
                    {fmtDuration(v.duration_seconds)}
                  </span>
                )}
              </div>
              <div className="p-4">
                {v.exercise_title && (
                  <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-light text-sky-hover text-[11px] font-bold mb-2">
                    {v.exercise_title}
                  </div>
                )}
                <h3 className="font-heading text-lg font-bold text-ink line-clamp-2">{v.title}</h3>
                {v.description && <p className="text-sm text-stone mt-1 line-clamp-2">{v.description}</p>}
              </div>
            </button>
          ))}
        </div>
      )}

      {active && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setActive(null)}>
          <div className="bg-white rounded-3xl max-w-3xl w-full p-4 card-tactile" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-heading text-xl font-bold text-ink">{active.title}</h3>
                {active.exercise_title && <div className="text-xs font-bold text-sky-hover mt-1">Related: {active.exercise_title}</div>}
              </div>
              <button data-testid="close-video-player" onClick={() => setActive(null)} className="text-stone hover:text-ink"><X className="w-5 h-5" /></button>
            </div>
            <VideoPlayer videoId={active.id} youtubeId={active.youtube_id} autoPlay onFirstPlay={() => onWatched(active.id)} />
            {active.description && <p className="mt-3 text-sm text-stone">{active.description}</p>}
          </div>
        </div>
      )}
    </>
  );
}
